/**
 * Shared parsing for custom-rule API validation errors.
 *
 * The svc-lux-loyalty endpoints return validation failures in two shapes:
 *  - Zod / handler-validator string items: `"<dotted.path>: <message>"`
 *    (e.g. `"matchConfig.ibsEarnKey: matchConfig.ibsEarnKey is required"`).
 *  - Schema-validation object items: `{ path, message, value? }`
 *    (e.g. `{ path: "rule.matchConfig.match.0.value", message: "Invalid regex pattern" }`).
 *
 * Earlier dialog code parsed only single-word field names (regex `^(\w+):...`),
 * so any nested/dotted path (notably `matchConfig.*`) silently failed to parse
 * and no inline error was shown. This helper accepts dotted paths and maps
 * `matchConfig.*` keys to the keys MatchConfigBuilder reads (`ibsEarnKey`,
 * `match.<index>.value`), while preserving last-segment behaviour for
 * top-level fields (`name`, `earnKey`, `identifierDescription`, ...).
 */

/** Object-format error item returned by schema validation. */
interface ApiErrorObjectItem {
  path: string;
  message: string;
  value?: string | object;
}

/** Result of parsing an API `errors` payload. */
export interface ParsedCustomRuleErrors {
  /** Field-keyed error map for inline display (keys match the form field names). */
  errors: Record<string, string>;
  /** True when at least one error item could not be parsed into a field key. */
  hadUnparsed: boolean;
}

/** Type guard for the object-format error item. */
function isApiErrorObjectItem(item: unknown): item is ApiErrorObjectItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof (item as ApiErrorObjectItem).path === 'string' &&
    typeof (item as ApiErrorObjectItem).message === 'string'
  );
}

/**
 * Maps a dotted server path to the field key the form/MatchConfigBuilder reads.
 *  - `matchConfig.ibsEarnKey` -> `ibsEarnKey`
 *  - `matchConfig.match.0.value` -> `match.0.value`
 *  - `rule.matchConfig.match.1.value` -> `match.1.value`
 *  - `rule.identifierDescription` -> `identifierDescription` (last segment)
 *
 * @param path - The dotted path from the server error.
 * @returns The field key used by the form.
 */
function pathToFieldKey(path: string): string {
  // Drop a leading `rule.` wrapper the schema validator adds for body fields.
  const withoutRule = path.startsWith('rule.') ? path.slice('rule.'.length) : path;

  // matchConfig errors keep their nested shape (minus the matchConfig prefix) so
  // they line up with the keys MatchConfigBuilder reads.
  if (withoutRule === 'matchConfig' || withoutRule.startsWith('matchConfig.')) {
    return withoutRule === 'matchConfig' ? 'matchConfig' : withoutRule.slice('matchConfig.'.length);
  }

  // Top-level fields use the last path segment (existing behaviour).
  const parts = withoutRule.split('.');
  return parts[parts.length - 1];
}

/**
 * Parses an API `errors` payload into a field-keyed map for inline display.
 *
 * @param apiErrors - The `errors` value from an ApiError (string[] or object[]).
 * @returns The parsed errors plus whether any item failed to parse.
 */
export function parseCustomRuleApiErrors(apiErrors: unknown): ParsedCustomRuleErrors {
  const errors: Record<string, string> = {};
  let hadUnparsed = false;

  if (!Array.isArray(apiErrors)) {
    // A non-array errors value means we have nothing per-field to show.
    return { errors, hadUnparsed: true };
  }

  apiErrors.forEach((item) => {
    if (typeof item === 'string') {
      // String format: "<dotted.path>: <message>" — allow dots in the path.
      const match = item.match(/^([\w.]+):\s*(.+)$/);
      if (match) {
        const [, path, message] = match;
        errors[pathToFieldKey(path)] = message;
      } else {
        hadUnparsed = true;
      }
    } else if (isApiErrorObjectItem(item)) {
      errors[pathToFieldKey(item.path)] = item.message;
    } else {
      hadUnparsed = true;
    }
  });

  return { errors, hadUnparsed };
}
